import { useState } from 'react';
import { Alert, api, Badge, Button, Card, DataTable, Input, Modal, Spinner, useAuth, useFetch, useForm } from '@greenrinse/ui';

export interface Country {
  id: string; code: string; name: string; currency: string; defaultTimezone: string; taxName: string; taxRateBps: number;
  taxInclusive: boolean; invoiceNumberPattern: string; plateRegex: string | null; isActive: boolean;
  supplierLegalName: string | null; supplierTaxIdLabel: string; supplierTaxId: string | null; supplierAddress: string | null;
}

const empty = { code: '', name: '', currency: '', defaultTimezone: 'UTC', taxName: 'VAT', taxRateBps: 0, taxInclusive: false, invoiceNumberPattern: 'GR-{COUNTRY}-{YYYY}-{SEQ:00000}', plateRegex: '', isActive: true,
  supplierLegalName: '', supplierTaxIdLabel: 'Tax ID', supplierTaxId: '', supplierAddress: '' };

export default function CountriesPage() {
  const { user } = useAuth();
  const list = useFetch<Country[]>('/api/countries?includeInactive=true');
  const [editing, setEditing] = useState<Country | 'new' | null>(null);
  const form = useForm<typeof empty>(empty);

  const open = (c: Country | 'new') => {
    form.reset(c === 'new' ? empty : { code: c.code, name: c.name, currency: c.currency, defaultTimezone: c.defaultTimezone, taxName: c.taxName, taxRateBps: c.taxRateBps, taxInclusive: c.taxInclusive, invoiceNumberPattern: c.invoiceNumberPattern, plateRegex: c.plateRegex ?? '', isActive: c.isActive,
      supplierLegalName: c.supplierLegalName ?? '', supplierTaxIdLabel: c.supplierTaxIdLabel, supplierTaxId: c.supplierTaxId ?? '', supplierAddress: c.supplierAddress ?? '' });
    setEditing(c);
  };

  const save = async () => {
    const ok = await form.submit(async (v) => {
      const body = { ...v, taxRateBps: Number(v.taxRateBps), plateRegex: v.plateRegex || null, supplierLegalName: v.supplierLegalName || null,
        supplierTaxIdLabel: v.supplierTaxIdLabel || null, supplierTaxId: v.supplierTaxId || null, supplierAddress: v.supplierAddress || null };
      if (editing === 'new') await api('/api/countries', { method: 'POST', body });
      else if (editing) await api(`/api/countries/${editing.id}`, { method: 'PUT', body });
    });
    if (ok) { setEditing(null); list.reload(); }
  };

  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Countries</h1>
        {user?.role === 'SuperAdmin' && <Button onClick={() => open('new')}>Add country</Button>}
      </div>
      <Card small style={{ padding: 0 }}>
        {list.loading ? <div style={{ padding: 24 }}><Spinner /></div> : list.error ? <Alert tone="error">{list.error}</Alert> : (
          <DataTable<Country>
            rows={list.data ?? []} rowKey={(c) => c.id} onRowClick={(c) => open(c)}
            columns={[
              { key: 'code', header: 'Code', render: (c) => <strong>{c.code}</strong>, width: 70 },
              { key: 'name', header: 'Name', render: (c) => c.name },
              { key: 'currency', header: 'Currency', render: (c) => c.currency },
              { key: 'tz', header: 'Timezone', render: (c) => c.defaultTimezone },
              { key: 'tax', header: 'Tax', render: (c) => `${c.taxName} ${(c.taxRateBps / 100).toFixed(2)}%${c.taxInclusive ? ' incl.' : ''}` },
              { key: 'supplier', header: 'Your tax no.', render: (c) => c.supplierTaxId ? `${c.supplierTaxIdLabel} ${c.supplierTaxId}` : c.taxRateBps > 0 ? <Badge tone="warning">Missing — can't issue invoices</Badge> : '—' },
              { key: 'status', header: '', render: (c) => c.isActive ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge> },
            ]}
          />
        )}
      </Card>

      <Modal open={editing !== null} title={editing === 'new' ? 'New country' : `Edit ${editing?.name ?? ''}`} onClose={() => setEditing(null)}>
        {form.message && <div style={{ marginBottom: 12 }}><Alert tone="error">{form.message}</Alert></div>}
        <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
          <Input label="ISO code" maxLength={2} {...form.bind('code')} placeholder="AE" />
          <Input label="Name" {...form.bind('name')} />
          <Input label="Currency (ISO 4217)" maxLength={3} {...form.bind('currency')} placeholder="AED" />
          <Input label="Default timezone (IANA)" {...form.bind('defaultTimezone')} placeholder="Asia/Dubai" />
          <Input label="Tax name" {...form.bind('taxName')} />
          <Input label="Tax rate (basis points, 500 = 5%)" type="number" min={0} max={10000} {...form.bind('taxRateBps')} />
          <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.taxInclusive} onChange={(e) => form.set('taxInclusive', e.target.checked)} /> Prices are tax-inclusive</label>
          <label className="gr-row" style={{ marginBottom: 16 }}><input type="checkbox" checked={form.values.isActive} onChange={(e) => form.set('isActive', e.target.checked)} /> Active</label>
          <div className="gr-span-2"><Input label="Invoice number pattern" hint="Tokens: {COUNTRY} {YYYY} {MM} {SEQ:00000}" {...form.bind('invoiceNumberPattern')} /></div>
          <div className="gr-span-2"><Input label="Plate validation regex (optional)" hint="Applied to the normalised plate (uppercase, no separators)" {...form.bind('plateRegex')} /></div>
          <div className="gr-span-2"><h3 style={{ margin: '8px 0 0' }}>Your company in this country</h3><p style={{ margin: '4px 0 0', opacity: 0.75 }}>Printed in the "From" block of tax invoices and credit notes. Required before issuing an invoice that charges tax.</p></div>
          <div className="gr-span-2"><Input label="Legal name" placeholder="GreenRinse Mobile Car Wash LLC" {...form.bind('supplierLegalName')} /></div>
          <Input label="Tax number label" hint="TRN (UAE), VAT No. (KSA), GSTIN (India)…" maxLength={20} {...form.bind('supplierTaxIdLabel')} />
          <Input label="Tax registration number" {...form.bind('supplierTaxId')} />
          <div className="gr-span-2"><Input label="Registered address" {...form.bind('supplierAddress')} /></div>
          <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" loading={form.busy}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
